import { useState } from "react";
import type { FormEvent } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/db6ckf22sgkaik1undmg";

type Status = "idle" | "submitting" | "success" | "error";

const topics = ["影视制作合作", "宣传发行", "品牌影像", "媒体采访", "其他咨询"];

export default function ContactForm() {
  const [status, setStatus] = useState<Status>("idle");
  const [feedback, setFeedback] = useState("");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);
    setFormError("");

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      setFeedback("感谢您的留言，我们会尽快与您联系。");
      form.reset();
      return;
    }

    const name = String(formData.get("name") || "").trim();
    const email = String(formData.get("email") || "").trim();
    const message = String(formData.get("message") || "").trim();

    if (!name) {
      setFormError("请填写您的姓名。");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setFormError("请填写有效的电子邮箱地址。");
      return;
    }
    if (!message) {
      setFormError("请填写留言内容。");
      return;
    }
    if (message.length > 500) {
      setFormError("留言内容不能超过 500 字。");
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      payload.append(key, String(value));
    });

    setStatus("submitting");
    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: {
        code?: string;
        meta?: { message?: string; detail?: string };
        message?: string;
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";

      const isSpam = typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");
      const isOk = response.ok && parsed?.code === "OK";

      if (isOk) {
        setStatus("success");
        setFeedback("提交成功，感谢您的留言，我们会尽快与您联系。");
        form.reset();
      } else if (isSpam) {
        setStatus("error");
        setFormError("提交未通过校验，请检查内容后重试。");
      } else {
        setStatus("error");
        setFormError(serverMsg || "提交失败，请稍后重试或通过电话与我们联系。");
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，提交失败，请稍后重试或通过电话与我们联系。");
    }
  };

  return (
    <form
      data-readdy-form
      id="contact-feedback-form"
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6"
    >
      <h2 className="font-heading text-lg font-bold text-foreground-950 md:text-xl">
        在线留言
      </h2>
      <p className="mt-2 text-sm text-foreground-600">
        填写以下信息，星光传媒团队将尽快与您取得联系。
      </p>

      <div className="mt-6 flex flex-col gap-4">
        <div className="flex flex-col gap-2">
          <label htmlFor="name" className="text-sm font-medium text-foreground-800">
            您的姓名 <span className="text-accent-600">*</span>
          </label>
          <input
            id="name"
            name="name"
            type="text"
            required
            placeholder="请输入您的姓名"
            className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-accent-400 focus:outline-none focus:ring-2 focus:ring-accent-200"
          />
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="email" className="text-sm font-medium text-foreground-800">
            电子邮箱 <span className="text-accent-600">*</span>
          </label>
          <input
            id="email"
            name="email"
            type="email"
            required
            placeholder="name@example.com"
            className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-accent-400 focus:outline-none focus:ring-2 focus:ring-accent-200"
          />
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="topic" className="text-sm font-medium text-foreground-800">
            咨询类型
          </label>
          <select
            id="topic"
            name="topic"
            defaultValue={topics[0]}
            className="rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 focus:border-accent-400 focus:outline-none focus:ring-2 focus:ring-accent-200"
          >
            {topics.map((topic) => (
              <option key={topic} value={topic}>
                {topic}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-2">
          <label htmlFor="message" className="text-sm font-medium text-foreground-800">
            留言内容 <span className="text-accent-600">*</span>
          </label>
          <textarea
            id="message"
            name="message"
            required
            maxLength={500}
            rows={5}
            placeholder="请简要描述您的需求或想了解的内容（不超过 500 字）"
            className="resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-accent-400 focus:outline-none focus:ring-2 focus:ring-accent-200"
          />
        </div>

        <div className="newsletter-alt-field" aria-hidden="true">
          <label htmlFor="website_alt">请勿填写此栏</label>
          <input
            id="website_alt"
            name="website_alt"
            type="text"
            tabIndex={-1}
            autoComplete="off"
            readOnly
          />
        </div>
      </div>

      {status === "success" && (
        <div className="mt-4 flex items-start gap-2 rounded-md bg-primary-50 px-4 py-3 text-sm text-primary-700">
          <i className="ri-checkbox-circle-line mt-0.5 text-base" />
          <span>{feedback}</span>
        </div>
      )}
      {status === "error" && formError && (
        <div className="mt-4 flex items-start gap-2 rounded-md bg-accent-50 px-4 py-3 text-sm text-accent-800">
          <i className="ri-error-warning-line mt-0.5 text-base" />
          <span>{formError}</span>
        </div>
      )}

      <button
        type="submit"
        disabled={status === "submitting"}
        className="mt-6 flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600 disabled:cursor-not-allowed disabled:opacity-60"
      >
        {status === "submitting" ? (
          <>
            <i className="ri-loader-4-line animate-spin" />
            提交中…
          </>
        ) : (
          <>
            <i className="ri-send-plane-fill" />
            提交留言
          </>
        )}
      </button>
    </form>
  );
}